/**
 * Two small pieces of geometry for the entity graph, kept apart from d3 so they can be tested: how to fit the graph in
 * its canvas, and which node an arrow key leads to.
 */

export interface Point {
  x: number;
  y: number;
}

export interface NodePoint extends Point {
  id: string;
}

export interface FitTransform {
  x: number;
  y: number;
  k: number;
}

export interface Padding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** The title bar sits on the top left of the canvas and Refresh on the top right, so the top keeps more room. */
export const FIT_PADDING: Padding = { top: 56, right: 24, bottom: 24, left: 24 };

/** A node is drawn up to this far from its centre (the largest threat diamond). */
const NODE_REACH = 16;

/**
 * The zoom and shift that put every node inside the canvas, centred in the room the padding leaves. It never zooms in
 * past `maxScale` (a graph of five nodes is not blown up) and never out past `minScale`. Null when there is nothing to fit.
 */
export function fitTransform(
  points: Point[],
  size: { width: number; height: number },
  padding: Padding = FIT_PADDING,
  maxScale = 1,
  minScale = 0.2
): FitTransform | null {
  if (points.length === 0) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - NODE_REACH;
  const maxX = Math.max(...xs) + NODE_REACH;
  const minY = Math.min(...ys) - NODE_REACH;
  const maxY = Math.max(...ys) + NODE_REACH;
  const roomW = Math.max(size.width - padding.left - padding.right, 1);
  const roomH = Math.max(size.height - padding.top - padding.bottom, 1);
  const k = Math.min(maxScale, Math.max(minScale, Math.min(roomW / (maxX - minX), roomH / (maxY - minY))));
  const centreX = padding.left + roomW / 2;
  const centreY = padding.top + roomH / 2;
  return {
    k,
    x: centreX - ((minX + maxX) / 2) * k,
    y: centreY - ((minY + maxY) / 2) * k,
  };
}

export type Direction = 'left' | 'right' | 'up' | 'down';

export const ARROW_DIRECTION: Record<string, Direction> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
};

/**
 * The node an arrow key moves to from `fromId`: the nearest one that lies on that side, counting a sideways offset twice
 * so the key follows the line it points along. Null when nothing lies that way, so the focus stays where it is.
 */
export function nextInDirection(points: NodePoint[], fromId: string, direction: Direction): string | null {
  const from = points.find((p) => p.id === fromId);
  if (!from) return null;
  let best: string | null = null;
  let bestScore = Infinity;
  for (const p of points) {
    if (p.id === fromId) continue;
    const dx = p.x - from.x;
    const dy = p.y - from.y;
    const [ahead, aside] =
      direction === 'right' ? [dx, dy] : direction === 'left' ? [-dx, dy] : direction === 'down' ? [dy, dx] : [-dy, dx];
    if (ahead <= 0) continue;
    const score = ahead + 2 * Math.abs(aside);
    if (score < bestScore) {
      bestScore = score;
      best = p.id;
    }
  }
  return best;
}
